import React from 'react';
import { motion } from 'framer-motion';
import { Palette, Zap, Sparkles } from 'lucide-react';
export function MechanismSection() {
  return <section className="py-24 bg-emerald-100 text-gray-900 relative overflow-hidden" data-id="element-154">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10" data-id="element-155">
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-156">
          <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-157" />
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-158">
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-159">
          <span className="text-violet-600 font-extrabold tracking-wider uppercase text-2xl md:text-4xl mb-4 block" data-id="element-160">
            NUESTRO SECRETO
          </span>
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6 bg-gradient-to-r from-orange-500 via-violet-600 to-emerald-500 bg-clip-text text-transparent leading-tight" data-id="element-161">
            El Método de Nutrición Camuflada
          </h2>

          {/* Mockup image */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="mb-8 rounded-3xl overflow-hidden shadow-2xl border-4 border-white" data-id="element-162">
            <img src="https://cdn.magicpatterns.com/uploads/553j9isUQEtN3hCQT6RRZ1/MOCKUP3heylloyd-image-1771694338775.png" alt="Mockup del libro de recetas en laptop, tablet y celular" className="w-full object-cover" data-id="element-163" />
          </motion.div>

          <p className="text-2xl md:text-3xl font-bold leading-relaxed" data-id="element-164">
            <span className="text-violet-600" data-id="element-165">
              No se trata de esconder mal,
            </span>{' '}
            <span className="text-orange-500" data-id="element-166">
              se trata de integrar inteligentemente.
            </span>{' '}
            <span className="text-emerald-600" data-id="element-167">
              El método que aprenderás combina tres claves científicas para
              burlar el radar "anti-verduras".
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-168">
          {/* Step 1 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} whileHover={{
          y: -8,
          scale: 1.02,
          transition: {
            type: 'spring',
            stiffness: 280,
            damping: 18
          }
        }} className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-emerald-200 shadow-md relative cursor-default" data-id="element-169">
            {/* Image */}
            <div className="relative h-48 overflow-hidden" data-id="element-170">
              <img src="https://cdn.magicpatterns.com/uploads/3hN9vXF4BT3TjyVncpVRyd/procesadorasfinalheylloyd-image-1771695038589.png" alt="Licuadora y procesadora con alimentos coloridos" className="w-full h-full object-cover" data-id="element-171" />
              <div className="absolute inset-0 bg-gradient-to-t from-violet-600/40 to-transparent" data-id="element-172" />
            </div>
            <div className="p-8 relative" data-id="element-173">
              <div className="text-4xl font-black text-emerald-200 absolute top-4 right-6" data-id="element-174">
                01
              </div>
              <div className="bg-violet-500 w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-white" data-id="element-175">
                <Zap className="h-6 w-6" data-id="element-176" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-violet-700" data-id="element-177">
                Texturas Invisibles
              </h3>
              <p className="text-gray-600" data-id="element-178">
                Procesado ultrafino de verduras hasta lograr que desaparezcan en
                la masa, salsas o rellenos. Sin trocitos, sin quejas.
              </p>
            </div>
          </motion.div>

          {/* Step 2 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} whileHover={{
          y: -8,
          scale: 1.02,
          transition: {
            type: 'spring',
            stiffness: 280,
            damping: 18
          }
        }} className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-orange-200 shadow-md relative cursor-default" data-id="element-179">
            {/* Image */}
            <div className="relative h-48 overflow-hidden" data-id="element-180">
              <img src="https://cdn.magicpatterns.com/uploads/f6Z11P6gEUMmE3nk3T9pBH/platos2heylloyd-image-1771695798847.png" alt="Platos coloridos con comida divertida para niños" className="w-full h-full object-cover" data-id="element-181" />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-500/40 to-transparent" data-id="element-182" />
            </div>
            <div className="p-8 relative" data-id="element-183">
              <div className="text-4xl font-black text-orange-200 absolute top-4 right-6" data-id="element-184">
                02
              </div>
              <div className="bg-orange-400 w-12 h-12 rounded-lg flex items-center justify-center mb-6 text-white" data-id="element-185">
                <Palette className="h-6 w-6" data-id="element-186" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-orange-600" data-id="element-187">
                Psicología del Color
              </h3>
              <p className="text-gray-600" data-id="element-188">
                Combinación estratégica de colores con los platos preferidos de
                tus hijos (rojos, dorados, blancos) para evitar el rechazo
                visual.
              </p>
            </div>
          </motion.div>

          {/* Step 3 */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} whileHover={{
          y: -8,
          scale: 1.02,
          transition: {
            type: 'spring',
            stiffness: 280,
            damping: 18
          }
        }} className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-emerald-200 shadow-md relative cursor-default" data-id="element-189">
            {/* Image */}
            <div className="relative h-48 overflow-hidden" data-id="element-190">
              <img src="https://cdn.magicpatterns.com/uploads/bhmWfrwbMCpJPoFJduRUEP/neutralizacionsaboresheylloyd-image-1771696062066.png" alt="Especias e ingredientes para neutralizar sabores vegetales" className="w-full h-full object-cover" data-id="element-191" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-600/40 to-transparent" data-id="element-192" />
            </div>
            <div className="p-8 relative" data-id="element-193">
              <div className="text-4xl font-black text-emerald-200 absolute top-4 right-6" data-id="element-194">
                03
              </div>
              <div className="bg-emerald-500 w-12 h-12 rounded-lg flex items-center justify-center mb-6 text-white" data-id="element-195">
                <Sparkles className="h-6 w-6" data-id="element-196" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-emerald-700" data-id="element-197">
                Neutralización de Sabor
              </h3>
              <p className="text-gray-600" data-id="element-198">
                Uso de especias suaves y combinaciones de ingredientes que
                neutralizan cualquier sabor vegetal fuerte o amargo.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}